// PickupPersonSheet — bottom-sheet form to add or edit a single
// authorised pickup person for a child.
//
// Mobile-first; 4-digit PIN is optional and stored in plaintext (it's
// a verbal token between parent and pickup person, not a security
// boundary). Photo upload is optional and reuses the child-photos
// bucket under `pickup/{child_id}/{ts}.jpg`.

import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Camera, Phone, ShieldCheck, TrashSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  createAuthorization,
  updateAuthorization,
  uploadPickupPhoto,
} from "@/lib/pickupAuthorizations";
import PickupPhoto from "@/components/pickup/PickupPhoto";

const RELATIONSHIPS = [
  "Grandparent",
  "Aunt / Uncle",
  "Sibling",
  "Family friend",
  "Neighbor",
  "Nanny / Sitter",
  "Carpool",
  "Other",
];

export default function PickupPersonSheet({
  open,
  onOpenChange,
  child, // {id, family_id, organization_id, first_name, last_name}
  existing, // null = create; row = edit
  addedBy,  // profileId
  onSaved,
}) {
  const isEdit = !!existing?.id;
  const [form, setForm] = useState(blank());
  const [photoPath, setPhotoPath] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        full_name: existing.full_name || "",
        relationship: existing.relationship || "",
        phone: existing.phone || "",
        pin: existing.pin || "",
        is_emergency_contact: !!existing.is_emergency_contact,
        notes: existing.notes || "",
      });
      setPhotoPath(existing.photo_path || null);
    } else {
      setForm(blank());
      setPhotoPath(null);
    }
  }, [open, existing]);

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoBusy(true);
    try {
      const newPath = await uploadPickupPhoto({
        childId: child.id,
        file,
        oldPath: photoPath || null,
      });
      setPhotoPath(newPath);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.message || "Could not upload photo");
    } finally {
      setPhotoBusy(false);
    }
  };

  const onSubmit = async () => {
    if (!form.full_name.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (form.pin && !/^\d{4}$/.test(form.pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const row = await updateAuthorization(existing.id, {
          full_name: form.full_name.trim(),
          relationship: form.relationship?.trim() || null,
          phone: form.phone?.trim() || null,
          pin: form.pin || null,
          is_emergency_contact: form.is_emergency_contact,
          notes: form.notes?.trim() || null,
          photo_path: photoPath,
        });
        toast.success(`Updated ${row.full_name}`);
        onSaved?.(row);
      } else {
        const row = await createAuthorization({
          organizationId: child.organization_id,
          childId: child.id,
          familyId: child.family_id,
          fullName: form.full_name,
          relationship: form.relationship,
          phone: form.phone,
          photoPath,
          pin: form.pin,
          isEmergencyContact: form.is_emergency_contact,
          notes: form.notes,
          addedBy,
        });
        toast.success(`Added ${row.full_name}`);
        onSaved?.(row);
      }
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="pickup-person-sheet"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">
            {isEdit ? "Edit pickup person" : "Add pickup person"}
          </SheetTitle>
          <SheetDescription>
            For{" "}
            <strong className="text-neutral-900">
              {[child?.first_name, child?.last_name].filter(Boolean).join(" ")}
            </strong>
            . Teachers and the office will see this person on the
            child's pickup list.
          </SheetDescription>
        </SheetHeader>

        {/* Photo */}
        <div className="mt-5 flex items-center gap-4">
          <PickupPhoto path={photoPath} name={form.full_name} size={64} />
          <div>
            <button
              type="button"
              data-testid="pickup-photo-upload"
              onClick={onPickFile}
              disabled={photoBusy}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
            >
              <Camera size={12} weight="duotone" />
              {photoBusy ? "Uploading…" : photoPath ? "Replace photo" : "Add photo"}
            </button>
            {photoPath && (
              <button
                type="button"
                data-testid="pickup-photo-clear"
                onClick={() => setPhotoPath(null)}
                className="ml-2 inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
              >
                <TrashSimple size={12} weight="bold" />
                Remove
              </button>
            )}
            <p className="mt-1 text-[11px] text-neutral-500">
              Optional. Helps teachers verify identity at pickup.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={onFile}
            data-testid="pickup-photo-input"
          />
        </div>

        {/* Form fields */}
        <div className="mt-5 grid grid-cols-1 gap-3">
          <Field label="Full name" required>
            <input
              type="text"
              data-testid="pickup-full-name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder="e.g. Sarah Cohen"
              className={INPUT_CLS}
              autoComplete="off"
            />
          </Field>

          <Field label="Relationship">
            <select
              data-testid="pickup-relationship"
              value={form.relationship}
              onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
              className={INPUT_CLS}
            >
              <option value="">Choose…</option>
              {RELATIONSHIPS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>

          <Field label="Phone">
            <div className="relative">
              <Phone
                size={14}
                weight="duotone"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                aria-hidden
              />
              <input
                type="tel"
                inputMode="tel"
                data-testid="pickup-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+1 555-0102"
                className={`${INPUT_CLS} pl-9`}
                autoComplete="off"
              />
            </div>
          </Field>

          <Field
            label="4-digit verification PIN"
            help="Optional. Share this with the pickup person verbally; teachers will check at pickup."
          >
            <div className="relative">
              <ShieldCheck
                size={14}
                weight="duotone"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                aria-hidden
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                data-testid="pickup-pin"
                value={form.pin}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setForm((f) => ({ ...f, pin: v }));
                }}
                placeholder="• • • •"
                className={`${INPUT_CLS} pl-9 tracking-[0.25em]`}
                autoComplete="off"
              />
            </div>
          </Field>

          <label className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-white p-3">
            <input
              type="checkbox"
              data-testid="pickup-emergency"
              checked={form.is_emergency_contact}
              onChange={(e) =>
                setForm((f) => ({ ...f, is_emergency_contact: e.target.checked }))
              }
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/20"
            />
            <span className="min-w-0 flex-1 text-sm text-neutral-800">
              Emergency contact
              <span className="block text-[11px] text-neutral-500">
                Teachers can call this person if you're unreachable.
              </span>
            </span>
          </label>

          <Field label="Notes">
            <textarea
              rows={2}
              data-testid="pickup-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. picks up on Wednesdays"
              className={INPUT_CLS}
            />
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="pickup-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="pickup-save"
            disabled={busy}
            onClick={onSubmit}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add person"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT_CLS =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, required, help, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
        {required && <span className="ml-0.5 text-[#C6851E]">*</span>}
      </span>
      {children}
      {help && <p className="mt-1 text-[11px] text-neutral-500">{help}</p>}
    </label>
  );
}

function blank() {
  return {
    full_name: "",
    relationship: "",
    phone: "",
    pin: "",
    is_emergency_contact: false,
    notes: "",
  };
}
